<template>
    <form>
        <div class="row q-col-gutter-md">
            <div class="col col-xs-12 col-md-4 col-lg-4">
                <q-select
                    v-model="PaymentMethodObj.country_id"
                    :label="$t('payment-methods.incoming.table_titles.country')"
                    emit-value
                    map-options
                    option-value="id"
                    option-label="name"
                    :options="countries"
                    :loading="loading"
                    :error="PaymentMethodObj.errors.has('country_id')"
                    :error-message="PaymentMethodObj.errors.get('country_id')"
                    :rules="[ val => val && val > 0 || $t('payment-methods.incoming.table_titles.country')+' is required!']"
                    color="input-color"
                >
                    <template v-slot:option="scope">
                        <q-item
                            v-bind="scope.itemProps"
                            v-on="scope.itemEvents"
                            style="border-bottom:1px solid #999"
                        >
                            <q-item-section avatar>
                                <q-icon :name="scope.opt.acronym" size="xs"/>
                            </q-item-section>
                            <q-item-section>
                                <q-item-label>{{ scope.opt.name }}</q-item-label>
                            </q-item-section>
                        </q-item>
                    </template>
                </q-select>
            </div> <!-- COUNTRY -->
            <div class="col col-xs-12 col-md-4 col-lg-4">
                <q-input
                    v-model="PaymentMethodObj.method"
                    :label="$t('payment-methods.incoming.table_titles.method')"
                    lazy-rules
                    :error="PaymentMethodObj.errors.has('method')"
                    :error-message="PaymentMethodObj.errors.get('method')"
                    :rules="[ val => val && val.length > 0 || $t('payment-methods.incoming.table_titles.method')+' is required!']"
                    color="input-color"
                />
            </div> <!-- METHOD -->

            <div class="col col-xs-12 col-md-4 col-lg-4">
                <q-select
                    clearable
                    v-model="PaymentMethodObj.page_template"
                    :label="$t('payment-methods.incoming.table_titles.page_template')"
                    emit-value
                    map-options
                    option-value="name"
                    option-label="name"
                    :options="page_templates"
                    :error="PaymentMethodObj.errors.has('page_template')"
                    :error-message="PaymentMethodObj.errors.get('page_template')"
                    color="input-color"
                />
            </div> <!-- Page Template -->

            <div class="col-12">
                <div class="q-pt-sm q-pb-xs">Instruction:</div>
                <q-editor
                    v-model="PaymentMethodObj.instructions"
                    :dense="$q.screen.lt.md"
                    :toolbar="[
                        [
                            {
                              label: $q.lang.editor.align,
                              icon: $q.iconSet.editor.align,
                              fixedLabel: true,
                              options: ['left', 'center', 'right', 'justify']
                            }
                        ],
                        ['bold', 'italic', 'strike', 'underline'],
                        ['hr', 'link'],
                        ['foreColor', 'backColor'],
                        ['unordered', 'ordered'],
                        ['fields'],
                        ['removeFormat', 'viewsource'],
                        ['undo', 'redo']
                    ]"
                    :fonts="{
                        arial: 'Arial',
                        arial_black: 'Arial Black',
                        comic_sans: 'Comic Sans MS',
                        courier_new: 'Courier New',
                        impact: 'Impact',
                        lucida_grande: 'Lucida Grande',
                        times_new_roman: 'Times New Roman',
                        verdana: 'Verdana'
                    }"
                    ref="editor"
                >
                    <template v-slot:direction>
                        <q-btn
                            color="primary"
                            style="width: 200px"
                            icon="fa fa-user"
                            @click="setRTL()"
                        />
                    </template>
                    <template slot="error">
                        {{PaymentMethodObj.errors.get('instructions')}}
                    </template>
                </q-editor>
            </div> <!-- INSTRUCTIONS -->

            <div class="col-12">
                <div class="q-pt-sm q-pb-xs">Description:</div>
                <q-editor
                    v-model="PaymentMethodObj.description"
                    :dense="$q.screen.lt.md"
                    :toolbar="[
                        [
                            {
                              label: $q.lang.editor.align,
                              icon: $q.iconSet.editor.align,
                              fixedLabel: true,
                              options: ['left', 'center', 'right', 'justify']
                            }
                        ],
                        ['bold', 'italic', 'strike', 'underline'],
                        ['hr', 'link'],
                        ['foreColor', 'backColor'],
                        ['unordered', 'ordered'],
                        ['fields'],
                        ['removeFormat', 'viewsource'],
                        ['undo', 'redo']
                    ]"
                    :fonts="{
                        arial: 'Arial',
                        arial_black: 'Arial Black',
                        comic_sans: 'Comic Sans MS',
                        courier_new: 'Courier New',
                        impact: 'Impact',
                        lucida_grande: 'Lucida Grande',
                        times_new_roman: 'Times New Roman',
                        verdana: 'Verdana'
                    }"
                    ref="editor"
                >
                    <template slot="error">
                        {{PaymentMethodObj.errors.get('description')}}
                    </template>
                </q-editor>
            </div> <!-- DESCRIPTION -->

            <div class="col col-xs-12 col-md-6 col-lg-6">
                <q-input
                    type="number"
                    v-model="PaymentMethodObj.transaction_fee"
                    :label="$t('payment-methods.incoming.table_titles.transaction_fee')"
                    lazy-rules
                    :error="PaymentMethodObj.errors.has('transaction_fee')"
                    :error-message="PaymentMethodObj.errors.get('transaction_fee')"
                    color="input-color"
                >
                    <template v-slot:append>
                        <q-icon name="fa fa-dollar-sign" />
                    </template>
                </q-input>
            </div> <!-- TRANSACTION FEE -->

            <div class="col col-xs-12 col-md-6 col-lg-6">
                <q-input
                    v-model="PaymentMethodObj.processing_time"
                    :label="$t('payment-methods.incoming.table_titles.processing_time')"
                    lazy-rules
                    :error="PaymentMethodObj.errors.has('processing_time')"
                    :error-message="PaymentMethodObj.errors.get('processing_time')"
                    :rules="[ val => val && val.length > 0 || $t('payment-methods.incoming.table_titles.processing_time') + ' is required!']"
                    color="primary"
                />
            </div> <!-- PROCESSING TIME -->

            <div class="col col-xs-12 col-sm-3 col-md-3">
                <p class="text-caption q-pb-xs" style="margin:0">{{$t('payment-methods.incoming.table_titles.is_default')}}</p>
                <q-toggle
                    v-model="PaymentMethodObj.is_default"
                    checked-icon="fa fa-check"
                    color="positive"
                    unchecked-icon="fa fa-times"
                    :label="PaymentMethodObj.is_default ? 'Yes' : 'No'"
                />
            </div> <!-- IS DEFAULT -->

            <div class="col col-xs-12 col-sm-3 col-md-3">
                <p class="text-caption q-pb-xs" style="margin:0">{{$t('payment-methods.incoming.table_titles.is_public')}}</p>
                <q-toggle
                    v-model="PaymentMethodObj.is_public"
                    checked-icon="fa fa-check"
                    color="positive"
                    unchecked-icon="fa fa-times"
                    :label="PaymentMethodObj.is_public ? 'Yes' : 'No'"
                />
            </div> <!-- IS PUBLIC -->

            <div class="col col-xs-12 col-sm-3 col-md-3">
                <p class="text-caption q-pb-xs" style="margin:0">{{$t('payment-methods.incoming.table_titles.is_fast')}}</p>
                <q-toggle
                    v-model="PaymentMethodObj.is_fast"
                    checked-icon="fa fa-check"
                    color="positive"
                    unchecked-icon="fa fa-times"
                    :label="PaymentMethodObj.is_fast ? 'Yes' : 'No'"
                />
            </div> <!-- IS FAST -->
        </div>
    </form>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import {IncomingPaymentMethodPagesMixin} from '../../mixins/IncomingPaymentMethodPagesMixin'

export default {
    name: 'Form',
    model: {
        prop: 'PaymentMethodObj',
        event: 'change'
    },
    props: {
        PaymentMethodObj: {
            type: Object,
            required: true
        }
    },
    mixins: [ IncomingPaymentMethodPagesMixin ],
    computed: {
        ...mapGetters(['countries']),
        page_templates() {
            return this.incoming_payment_method_page_templates
        }
    },
    data() {
        return {
            loading: false
        }
    },
    methods: {
        ...mapActions(['set_countries']),
        submit() {
            this.$emit('submit')
        },
        setRTL() {
            const edit = this.$refs.editor
            edit.caret.restore()
            let selection = window.getSelection().toString();
            let wrappedselection = '<span style="direction: rtl">' + selection + '</span>'
            edit.runCmd('insertHTML', wrappedselection)
            edit.focus()
        },
    },
    created() {
        if (! this.countries.length) {
            this.loading = true
            this.set_countries().then(() => {this.loading = false}).catch(() => {this.loading = false})
        }
    },
    components: {},
}
</script>

<style scoped>

</style>
